import { useState, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import InsightList from './insightList.component'
import PaginationFooter from './paginationFooter.component'
import { AlertBadge, ListWrapper } from './insightList.styles'
import { ITEMS_PER_PAGE } from './insight.constants'
import { FormattedInsight } from './insightList.screen'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartSimple } from '@fortawesome/free-solid-svg-icons'
import { InsightDetail } from './insightDetail.component'
import { Insight } from '~/packages/general/variable/variables.typing'

const ViewButton = styled.button`
  cursor: pointer;
  background-color: transparent;
  border: none;
  padding: 0.5rem 0.75rem;
  margin: 0;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`

interface InsightListWithPaginationProps {
  formattedInsights: FormattedInsight[]
  totalCount: number
  isLoading: boolean
  onPageChange: (page: number) => void
  onItemClick?: (item: FormattedInsight) => void
  expandedId?: string
  expandedLoading?: boolean
  expandedContent?: React.ReactNode
  onSortChange?: (key: string, order: 'asc' | 'desc') => void
  currentSort?: { key: string; order: 'asc' | 'desc' }
}

export function InsightListWithPagination({
  formattedInsights,
  totalCount,
  isLoading,
  onPageChange,
  onItemClick,
  expandedId,
  expandedLoading,
  expandedContent,
  onSortChange,
  currentSort,
}: InsightListWithPaginationProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [sortKey, setSortKey] = useState<string | undefined>(currentSort?.key)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(currentSort?.order || 'desc')
  const [width, setWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 0)
  const [detailInsight, setDetailInsight] = useState<{
    insight: Insight
    x: number
    y: number
  } | null>(null)

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page)
      onPageChange(page)
    },
    [onPageChange],
  )

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = currentPage * ITEMS_PER_PAGE
  const currentPageData = formattedInsights.slice(startIndex, endIndex)

  const handleSortChange = useCallback(
    (key: string) => {
      let nextKey: string | undefined
      let nextOrder: 'asc' | 'desc' = 'asc'

      // Ciclo: ASC -> DESC -> null (sem ordenação)
      if (sortKey === key) {
        if (sortOrder === 'asc') {
          nextKey = key
          nextOrder = 'desc'
        } else {
          nextKey = undefined
          nextOrder = 'desc'
        }
      } else {
        nextKey = key
        nextOrder = 'asc'
      }

      setSortKey(nextKey)
      setSortOrder(nextOrder)

      // Reset to first page on sort change
      setCurrentPage(1)
      onPageChange(1)

      if (onSortChange) {
        onSortChange(nextKey || '', nextOrder)
      }
    },
    [onPageChange, onSortChange, sortKey, sortOrder],
  )

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE)

  // Base labels and fractions
  let labels: Record<string, string> = {
    description: 'Descrição',
    variable: 'Variável',
    context: 'Contexto',
    alertType: 'Tipo de alerta',
    alertBadge: 'Alerta',
    view: 'Visualizar',
  }

  let fractions: Record<string, number> = {
    description: 0.2533,
    variable: 0.16,
    context: 0.2533,
    alertType: 0.1778,
    alertBadge: 0.1556,
    view: 0.0622,
  }

  const hideAlertType = width < 1400
  const hideAlertBadge = width < 900
  const hideView = width <= 768

  let hiddenFieldsForResponsive: string[] = []

  if (hideAlertType) {
    hiddenFieldsForResponsive.push('alertType')
  }
  if (hideAlertBadge) {
    hiddenFieldsForResponsive.push('alertBadge')
  }
  if (hideView) {
    hiddenFieldsForResponsive.push('view')
  }

  // Remove hidden fields from labels and fractions, redistributing width
  hiddenFieldsForResponsive.forEach(field => {
    delete labels[field]
    if (fractions[field]) {
      const hiddenWidth = fractions[field]
      delete fractions[field]

      // Redistribute width to visible fields
      if (field === 'alertType') {
        fractions.description += hiddenWidth * 0.4
        fractions.context += hiddenWidth * 0.6
      } else if (field === 'alertBadge') {
        fractions.description = 0.35
        fractions.variable = 0.2
        fractions.context = 0.45
      }
    }
  })

  // Se apenas 3 colunas, dividir igualmente
  const visibleFieldCount = Object.keys(labels).length
  if (visibleFieldCount === 3) {
    const equalFraction = 1 / 3
    Object.keys(labels).forEach(key => {
      fractions[key] = equalFraction
    })
  }

  const customCells: Record<string, (item: FormattedInsight) => React.ReactNode> = {
    alertBadge: (item: FormattedInsight) =>
      item.alertBadge ? (
        <AlertBadge $bgColor={item.alertBadge.color} $textColor={item.alertBadge.textColor}>
          {item.alertBadge.label}
        </AlertBadge>
      ) : null,
    variable: (item: FormattedInsight) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#142E52' }}>
          {item.variable.title}
        </div>
        {item.variable.label && (
          <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{item.variable.label}</div>
        )}
      </div>
    ),
    context: (item: FormattedInsight) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#142E52' }}>
          {item.context.delimitation || '-'}
        </div>
        {item.context.updatedAt && (
          <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
            Atualizado em {new Date(item.context.updatedAt).toLocaleDateString('pt-BR')}
          </div>
        )}
      </div>
    ),
    view: (item: FormattedInsight) => {
      const rendererType = item.insight?.visualizations?.[0]?.renderer
      if (
        !rendererType ||
        !['timeSeriesComparison', 'timeSeriesWithBands', 'outbreakDetectionPlot'].includes(
          rendererType,
        )
      ) {
        return '-'
      }

      return item.insight ? (
        <ViewButton
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()
            const rect = e.currentTarget.getBoundingClientRect()
            setDetailInsight({
              insight: item.insight as Insight,
              x: rect.left,
              y: rect.top + rect.height / 2,
            })
          }}
        >
          <FontAwesomeIcon icon={faChartSimple} size='xl' />
        </ViewButton>
      ) : (
        <></>
      )
    },
  }

  const onItemClickInternal = useCallback(
    (item: FormattedInsight) => {
      if (onItemClick) {
        onItemClick(item)
      }

      if (width < 900) {
        setDetailInsight({
          insight: item.insight as Insight,
          x: 0,
          y: 0,
        })
      }
    },
    [onItemClick, width],
  )

  return (
    <ListWrapper>
      <InsightList
        isLoading={isLoading}
        items={currentPageData}
        labels={labels}
        fractions={fractions}
        customCells={customCells}
        customCellPadding='12px 12px 12px 0'
        onItemClick={onItemClickInternal}
        expandedId={expandedId}
        expandedLoading={expandedLoading}
        expandedContent={expandedContent}
        onSortChange={handleSortChange}
        currentSort={currentSort || (sortKey ? { key: sortKey, order: sortOrder } : undefined)}
        hiddenFields={hiddenFieldsForResponsive}
      />
      <PaginationFooter
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalCount}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={handlePageChange}
      />

      {detailInsight && (
        <InsightDetail
          insight={detailInsight.insight}
          x={detailInsight.x}
          y={detailInsight.y}
          onClose={() => setDetailInsight(null)}
        />
      )}
    </ListWrapper>
  )
}

interface ListWithAutoLoadProps {
  formattedInsights: FormattedInsight[]
  totalCount: number
  loadedCount: number
  isLoading: boolean
  onPageChange: (page: number) => void
  onItemClick?: (item: FormattedInsight) => void
  expandedId?: string
  expandedLoading?: boolean
  expandedContent?: React.ReactNode
  onSortChange?: (key: string, order: 'asc' | 'desc') => void
  currentSort?: { key: string; order: 'asc' | 'desc' }
}

export function ListWithAutoLoad({
  formattedInsights,
  totalCount,
  loadedCount,
  isLoading,
  onPageChange,
  onItemClick,
  expandedId,
  expandedLoading,
  expandedContent,
  onSortChange,
  currentSort,
}: ListWithAutoLoadProps) {
  const isLoadingRef = useRef(false)

  const handleInternalPageChange = useCallback(
    (page: number) => {
      const neededItems = page * ITEMS_PER_PAGE

      // Carregar mais dados se necessário
      if (neededItems > loadedCount && loadedCount < totalCount && !isLoadingRef.current) {
        isLoadingRef.current = true
        onPageChange(page)
      }
    },
    [loadedCount, totalCount, onPageChange],
  )

  // Reset flag de loading quando dados chegarem
  useEffect(() => {
    if (!isLoading) {
      isLoadingRef.current = false
    }
  }, [isLoading])

  return (
    <InsightListWithPagination
      formattedInsights={formattedInsights}
      totalCount={totalCount}
      isLoading={isLoading}
      onPageChange={handleInternalPageChange}
      onItemClick={onItemClick}
      expandedId={expandedId}
      expandedLoading={expandedLoading}
      expandedContent={expandedContent}
      onSortChange={onSortChange}
      currentSort={currentSort}
    />
  )
}
